import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import { useLanguage, LANGUAGES } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '../ui/navigation-menu';

const LOGO_URL = 'https://caltavuturoclimbing.it/api/uploads/20260429172156_82f8de2b.png ';

const CRAGS_MENU = [
  { name: 'Falesia di Gazzara', slug: 'gazzara' },
  { name: 'Falesia di Cabeci', slug: 'cabeci' },
  { name: 'Settore di Cabeci Tinello', slug: 'cabeci-tinello' },
  { name: 'Settore di Cabeci L\'Alveare', slug: 'cabeci-alveare' },
  { name: 'Falesia Rocca Piccola', slug: 'rocca-piccola' },
  { name: 'Falesia Rocca di Sciara', slug: 'rocca-di-sciara' },
  { name: 'Falesia Via del Gatto', slug: 'via-del-gatto' },
];

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t, language, changeLanguage, languages } = useLanguage();
  const { isAuthenticated, logout, user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const isActive = (path) => location.pathname === path;

  return (
    <nav 
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white/90 backdrop-blur-sm'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Caltavuturo Climbing" 
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <Link 
                    to="/" 
                    className={`px-4 py-2 font-heading text-sm uppercase tracking-wider transition-colors ${
                      isActive('/') ? 'text-[#FF6D00]' : 'text-gray-800 hover:text-[#FF6D00]'
                    }`}
                  >
                    {t('home')}
                  </Link>
                </NavigationMenuItem>

                {/* Team Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="font-heading text-sm uppercase tracking-wider bg-transparent">
                    {t('team')}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-48 gap-1 p-2">
                      <li>
                        <NavigationMenuLink asChild>
                          <Link to="/team/about" className="block px-4 py-2 text-sm hover:bg-gray-100 rounded">
                            {t('aboutUs')}
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link to="/team/projects" className="block px-4 py-2 text-sm hover:bg-gray-100 rounded">
                            {t('projects')}
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link to="/team/events" className="block px-4 py-2 text-sm hover:bg-gray-100 rounded">
                            {t('events')}
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link to="/team/blog" className="block px-4 py-2 text-sm hover:bg-gray-100 rounded">
                            {t('blog')}
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link to="/team/gallery" className="block px-4 py-2 text-sm hover:bg-gray-100 rounded">
                            {t('gallery')}
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Crags Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="font-heading text-sm uppercase tracking-wider bg-transparent">
                    {t('crags')}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-64 gap-1 p-2">
                      {CRAGS_MENU.map((crag) => (
                        <li key={crag.slug}>
                          <NavigationMenuLink asChild>
                            <Link 
                              to={`/crags/${crag.slug}`} 
                              className="block px-4 py-2 text-sm hover:bg-gray-100 rounded"
                            >
                              {crag.name}
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link 
                    to="/how-to-arrive" 
                    className={`px-4 py-2 font-heading text-sm uppercase tracking-wider transition-colors ${
                      isActive('/how-to-arrive') ? 'text-[#FF6D00]' : 'text-gray-800 hover:text-[#FF6D00]'
                    }`}
                  >
                    {t('howToArrive')}
                  </Link>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link 
                    to="/accommodation" 
                    className={`px-4 py-2 font-heading text-sm uppercase tracking-wider transition-colors ${
                      isActive('/accommodation') ? 'text-[#FF6D00]' : 'text-gray-800 hover:text-[#FF6D00]'
                    }`}
                  >
                    {t('accommodation')}
                  </Link>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link 
                    to="/donations" 
                    className={`px-4 py-2 font-heading text-sm uppercase tracking-wider transition-colors ${
                      isActive('/donations') ? 'text-[#FF6D00]' : 'text-gray-800 hover:text-[#FF6D00]'
                    }`}
                  >
                    {t('donations')}
                  </Link>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link 
                    to="/contact" 
                    className={`px-4 py-2 font-heading text-sm uppercase tracking-wider transition-colors ${
                      isActive('/contact') ? 'text-[#FF6D00]' : 'text-gray-800 hover:text-[#FF6D00]'
                    }`}
                  >
                    {t('contact')}
                  </Link>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center px-3 py-2 text-sm hover:text-[#FF6D00] transition-colors" data-testid="language-selector">
                <Globe className="w-4 h-4 mr-1" />
                <span className="uppercase">{language}</span>
                <ChevronDown className="w-3 h-3 ml-1" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {Object.values(languages).map((lang) => (
                  <DropdownMenuItem 
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={language === lang.code ? 'bg-gray-100' : ''}
                    data-testid={`lang-${lang.code}`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Admin Link */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="ml-2 px-4 py-2 bg-[#FF6D00] text-white rounded text-sm font-heading uppercase">
                  {user?.name || 'Admin'}
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to="/admin">{t('dashboard')}</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout}>
                    {t('logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link 
                to="/admin/login" 
                className="ml-2 px-4 py-2 border border-gray-300 rounded text-sm font-heading uppercase hover:border-[#FF6D00] hover:text-[#FF6D00] transition-colors"
                data-testid="admin-login-link"
              >
                Admin
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2" 
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden bg-white border-t max-h-[calc(100vh-5rem)] overflow-y-auto" data-testid="mobile-menu">
          <div className="container-custom py-4 space-y-2 pb-8">
            <Link to="/" className="block py-2 font-heading uppercase">{t('home')}</Link>
            
            <div className="border-t pt-2">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">{t('team')}</p>
              <Link to="/team/about" className="block py-2 pl-4">{t('aboutUs')}</Link>
              <Link to="/team/projects" className="block py-2 pl-4">{t('projects')}</Link>
              <Link to="/team/events" className="block py-2 pl-4">{t('events')}</Link>
              <Link to="/team/blog" className="block py-2 pl-4">{t('blog')}</Link>
              <Link to="/team/gallery" className="block py-2 pl-4">{t('gallery')}</Link>
            </div>

            <div className="border-t pt-2">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">{t('crags')}</p>
              {CRAGS_MENU.map((crag) => (
                <Link key={crag.slug} to={`/crags/${crag.slug}`} className="block py-2 pl-4">
                  {crag.name}
                </Link>
              ))}
            </div>

            <div className="border-t pt-2">
              <Link to="/how-to-arrive" className="block py-2 font-heading uppercase">{t('howToArrive')}</Link>
              <Link to="/accommodation" className="block py-2 font-heading uppercase">{t('accommodation')}</Link>
              <Link to="/donations" className="block py-2 font-heading uppercase">{t('donations')}</Link>
              <Link to="/contact" className="block py-2 font-heading uppercase">{t('contact')}</Link>
            </div>

            {/* Mobile Language Selector */}
            <div className="border-t pt-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Language</p>
              <div className="flex flex-wrap gap-2">
                {Object.values(languages).map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`px-3 py-1 rounded text-sm ${
                      language === lang.code ? 'bg-[#FF6D00] text-white' : 'bg-gray-100'
                    }`}
                  >
                    {lang.flag} {lang.code.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            {isAuthenticated ? (
              <div className="border-t pt-4 space-y-2">
                <Link to="/admin" className="block py-2 bg-[#FF6D00] text-white text-center rounded">
                  {t('dashboard')}
                </Link>
                <button onClick={logout} className="block w-full py-2 border rounded text-center">
                  {t('logout')}
                </button>
              </div>
            ) : (
              <Link to="/admin/login" className="block py-2 mt-4 border rounded text-center">
                Admin Login
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
